/**
 * OPIS TYPU WNIOSKU DLA PRACOWNIKA — „co to za wniosek, kiedy go składać, co będzie potrzebne, kto akceptuje”.
 *
 * [polecenie właściciela 2026-09-28] „w oknie Utworz nowe zgloszenie - zrob 2 karty - sprawy techniczne, wnioski
 * pracownicze; […] jak wnioski pracownicze, to co mozna zlozyc w sekcji mpracownik.krosno.com/wnioski-hr - nowy
 * wniosek w takim samym wygladzie jak zgloszenie z opisem jaki wniosek co i jak (ale z opcja wyboru kategorii
 * a potem listy)”.
 *
 * Opis składa się WYŁĄCZNIE z danych typu, które przysyła serwer (`GET /hr-requests/available-types`): opis
 * z konfiguratora, pola formularza (wymagane, załączniki), pula salda, jednorazowość, obowiązek rozliczenia i czas
 * realizacji. Nie ma tu drugiej konfiguracji typów — kadry zmieniają typ w /admin/hr-config i opis zmienia się sam.
 * Brak opisu w konfiguratorze = tekst zastępczy z nazwy i kategorii (pracownik nie dostaje pustego miejsca).
 *
 * [poprawka po przeglądzie 2026-09-28] ŚCIEŻKI AKCEPTACJI LISTA TYPÓW NIE NIESIE — celowo (HrAvailableTypeDto:
 * „kuchnia kadr”). Pierwsza wersja czytała `approvalSteps`/`approvalFlow`, których serwer tu nie wysyła, i każdemu
 * typowi podstawiała „przełożony → HR” (także zaświadczeniu tylko dla kadr i wnioskowi realizowanemu automatycznie),
 * a zapadka tego nie widziała, bo atrapy miały pola, których produkcja nie zna. Teraz „kto akceptuje” pochodzi
 * WYŁĄCZNIE z kroków, które serwer utworzył przy zapisie wniosku (`ktoAkceptujeZlozonyWniosek`); przed złożeniem
 * ekran mówi uczciwie, gdzie tę ścieżkę zobaczyć (`KTO_AKCEPTUJE_PRZED_ZLOZENIEM`). To samo z pionem obsługi
 * (`orgGroup`): serwer go nie wysyła, więc tekst zastępczy nie zgaduje „do działu HR/kadr”.
 *
 * Moduł jest CZYSTY (bez Reacta i bez sieci), żeby dało się go zmierzyć dla każdego kształtu typu bez montowania okna.
 */
import { FIELD_TYPE, WITHDRAWAL_TYPE_CODE } from "../constants/hrRequestTypes";
import { czyBrakOsoby, TEKST_BRAKU_OSOBY } from "./hrBrakOsoby";

/* Nazwy ról kroku ścieżki akceptacji — te same, które konfigurator typu (/admin/hr-config → Typ wniosku →
   Ścieżka akceptacji) wpisuje do kroku jako `label`. Tu są wyłącznie zapasem dla kroku ZŁOŻONEGO wniosku zapisanego
   bez etykiety. */
const ROLA_KROKU = {
    manager: "Bezpośredni przełożony",
    director: "Dyrektor działu",
    hr: "Dział Kadr (HR)",
    cfo: "Dyrektor finansowy",
    president: "Prezes / Zarząd",
    employee_ack: "Pracownik (potwierdzenie)",
    accounting: "Księgowość (rozliczenia)",
};

/** Polska odmiana liczebnika: 1 pole, 2–4 pola, 5–21 pól, 22–24 pola… */
export const odmiana = (n, [jeden, kilka, wiele]) => {
    const x = Math.abs(Number(n) || 0);
    if (x === 1) return jeden;
    const d = x % 10;
    const s = x % 100;
    if (d >= 2 && d <= 4 && (s < 12 || s > 14)) return kilka;
    return wiele;
};

const tekst = (v) => (typeof v === "string" ? v.trim() : "");

const POLA_BEZ_WARTOSCI = new Set([FIELD_TYPE.INFO, FIELD_TYPE.HIDDEN]);
const POLA_DAT = new Set([FIELD_TYPE.DATE, FIELD_TYPE.DATE_RANGE, FIELD_TYPE.DATETIME]);

/** Pola, które pracownik faktycznie wypełnia (bez opisów, pól ukrytych i wyliczanych przez system). */
const polaDoWypelnienia = (typ) =>
    (Array.isArray(typ?.fields) ? typ.fields : []).filter((f) => f && !POLA_BEZ_WARTOSCI.has(f.type) && !f.computed);

/**
 * KTO AKCEPTUJE PRZED ZŁOŻENIEM — lista typów nie niesie ścieżki akceptacji (decyzja serwera, patrz nagłówek), więc
 * nie zgadujemy. Zdanie mówi, GDZIE ją zobaczyć: potwierdzenie po wysłaniu i szczegóły wniosku pokazują kroki, które
 * serwer naprawdę utworzył dla tej osoby.
 */
export const KTO_AKCEPTUJE_PRZED_ZLOZENIEM =
    "Kto i w jakiej kolejności akceptuje wniosek, zobaczysz zaraz po jego wysłaniu — w potwierdzeniu i w szczegółach wniosku.";

/**
 * KTO AKCEPTUJE ZŁOŻONY WNIOSEK — kroki z odpowiedzi serwera na zapis (`POST /hr-requests` → `approvalSteps`,
 * HrApprovalStepDto: `stepOrder`, `role`, `mode`, `label`, `assignedUserName`). Tylko kroki blokujące (tryb
 * „approval”) w kolejności; krok „do wiadomości” i potwierdzenie pracownika nie są akceptacją. Przy kroku z przypisaną
 * osobą dopisujemy ją w nawiasie — to ta sama osoba, którą pokażą szczegóły wniosku.
 * @returns {string} "" = serwer nie przysłał ani jednego kroku akceptacji
 */
export const ktoAkceptujeZlozonyWniosek = (wniosek) =>
    (Array.isArray(wniosek?.approvalSteps) ? wniosek.approvalSteps : [])
        .filter((k) => k && (k.mode || "approval") === "approval")
        .slice()
        .sort((a, b) => (Number(a.stepOrder ?? a.order) || 0) - (Number(b.stepOrder ?? b.order) || 0))
        .map((k) => {
            const rola = tekst(k.label) || ROLA_KROKU[k.role] || tekst(k.role);
            const osoba = tekst(k.assignedUserName);
            return osoba && rola && osoba !== rola ? `${rola} (${osoba})` : rola || osoba;
        })
        .filter(Boolean)
        .join(" → ");

/**
 * CO DALEJ ZE ZŁOŻONYM WNIOSKIEM — zdanie na ekran potwierdzenia, WYŁĄCZNIE z odpowiedzi serwera: zrealizowany od razu
 * (typ bez akceptacji) / trafił do akceptacji: <prawdziwe kroki> / serwer nie podał kroków → gdzie je zobaczyć.
 */
export const coDalejZWnioskiem = (wniosek, nazwaTypu = "") => {
    const nazwa = tekst(nazwaTypu) ? `„${tekst(nazwaTypu)}”` : "Wniosek";
    const status = wniosek?.status;
    if (status === "Completed" || status === 8) {
        return `${nazwa} został zrealizowany od razu po złożeniu — nie wymaga akceptacji.`;
    }
    const kto = ktoAkceptujeZlozonyWniosek(wniosek);
    if (kto) return `${nazwa} trafił do akceptacji: ${kto}.`;
    return `${nazwa} trafił do obsługi — kto i w jakiej kolejności go akceptuje, zobaczysz w szczegółach wniosku.`;
};

/** CO TO ZA WNIOSEK — opis z konfiguratora, a bez niego zdanie z nazwy i kategorii (bez zgadywania pionu obsługi). */
export const coToZaWniosek = (typ, nazwaKategorii = "") => {
    const opis = tekst(typ?.description);
    if (opis) return opis;
    const nazwa = tekst(typ?.label) || "Wniosek";
    const kategoria = tekst(nazwaKategorii) || tekst(typ?._catLabel) || tekst(typ?.categoryLabel);
    return kategoria
        ? `„${nazwa}” — wniosek pracowniczy z kategorii „${kategoria}”.`
        : `„${nazwa}” — wniosek pracowniczy.`;
};

/** KIEDY GO SKŁADAĆ — z cech typu: pula salda, daty, jednorazowość, rozliczenie, czas realizacji. */
export const kiedySkladacWniosek = (typ) => {
    if (!typ) return "";
    if (typ.code === WITHDRAWAL_TYPE_CODE) {
        return "Gdy chcesz wycofać wniosek, który został już zrealizowany (w oknie wycofania od jego realizacji).";
    }
    const zdania = [];
    const maDaty = polaDoWypelnienia(typ).some((f) => POLA_DAT.has(f.type));
    if (typ.limitPool) {
        zdania.push("Przed planowaną nieobecnością — wniosek zużywa Twoją pulę dni, a formularz pokaże, ile zostanie.");
    } else if (maDaty) {
        zdania.push("Przed terminem, którego dotyczy — w formularzu wskażesz datę albo okres.");
    }
    if (typ.isOneTime) zdania.push("Składasz go jednorazowo.");
    if (typ.wymagaRozliczenia) {
        const dni = Number(typ.terminRozliczeniaDni) || 14;
        zdania.push(`Po zakończeniu trzeba go rozliczyć (w ciągu ${dni} ${odmiana(dni, ["dnia", "dni", "dni"])}).`);
    }
    const godziny = Number(typ.godzinNaRealizacje) || 0;
    if (godziny > 0) {
        zdania.push(`Kadry realizują go w ciągu ${godziny} ${odmiana(godziny, ["godziny", "godzin", "godzin"])} od złożenia.`);
    }
    if (zdania.length === 0) zdania.push("W dowolnym momencie, gdy sprawa Cię dotyczy.");
    return zdania.join(" ");
};

/** CO BĘDZIE POTRZEBNE — pola obowiązkowe (bez warunkowych) i załączniki, z odmianą liczebnika. */
export const coBedziePotrzebne = (typ, maksNazw = 4) => {
    if (!typ) return "";
    if (typ.code === WITHDRAWAL_TYPE_CODE) return "Numer wniosku do wycofania (wybierzesz go z listy) i powód wycofania.";
    const pola = polaDoWypelnienia(typ);
    const zalaczniki = pola.filter((f) => f.type === FIELD_TYPE.FILE);
    const wymagane = pola.filter((f) => f.required && f.type !== FIELD_TYPE.FILE && !f.showIf);
    const warunkowe = pola.filter((f) => f.required && f.showIf).length;
    const zdania = [];
    if (wymagane.length > 0) {
        const nazwy = wymagane.slice(0, maksNazw).map((f) => tekst(f.label) || f.name).filter(Boolean);
        const reszta = wymagane.length - nazwy.length;
        zdania.push(`Wypełnisz: ${nazwy.join(", ")}${reszta > 0 ? ` i ${reszta} ${odmiana(reszta, ["inne pole", "inne pola", "innych pól"])}` : ""}.`);
    }
    if (warunkowe > 0) {
        zdania.push(`${warunkowe} ${odmiana(warunkowe, ["pole pojawia się", "pola pojawiają się", "pól pojawia się"])} zależnie od wyboru.`);
    }
    if (zalaczniki.length > 0) {
        const opisy = zalaczniki.map((f) => `${tekst(f.label) || "plik"} (${f.required ? "wymagany" : "opcjonalny"})`);
        zdania.push(`${zalaczniki.length === 1 ? "Załącznik" : "Załączniki"}: ${opisy.join(", ")}.`);
    }
    if (zdania.length === 0) {
        return pola.length > 0
            ? "Formularz nie ma pól obowiązkowych — uzupełnisz tylko to, co Cię dotyczy."
            : "Nic poza potwierdzeniem złożenia — formularz nie ma pól do wypełnienia.";
    }
    return zdania.join(" ");
};

/**
 * Komplet odpowiedzi dla karty typu w oknie „Utwórz nowe zgłoszenie → Wnioski pracownicze”: co to, kiedy, co potrzebne.
 * „Kto akceptuje” NIE jest tu liczone z typu (serwer go nie wysyła) — lista pokazuje raz `KTO_AKCEPTUJE_PRZED_ZLOZENIEM`,
 * a prawdziwe kroki pojawiają się po wysłaniu (`coDalejZWnioskiem`).
 */
export const opisTypuWniosku = (typ, nazwaKategorii = "") => ({
    co: coToZaWniosek(typ, nazwaKategorii),
    kiedy: kiedySkladacWniosek(typ),
    coPotrzebne: coBedziePotrzebne(typ),
});

/**
 * OPIS KAFLA KATEGORII — z nazw jej typów (kategoria wniosków nie ma w bazie własnego opisu: HrCategory/
 * HrAvailableCategoryDto to kod, nazwa, ikona i kolejność). Pierwsze nazwy + „i N innych”, żeby kafel mówił,
 * co jest w środku, zamiast powtarzać nazwę kategorii.
 */
export const opisKategoriiWnioskow = (kategoria, maksNazw = 3) => {
    const nazwy = (Array.isArray(kategoria?.types) ? kategoria.types : [])
        .map((t) => tekst(t?.label) || tekst(t?.shortLabel))
        .filter(Boolean);
    if (nazwy.length === 0) return "";
    const pokazane = nazwy.slice(0, maksNazw);
    const reszta = nazwy.length - pokazane.length;
    return `${pokazane.join(", ")}${reszta > 0 ? ` i ${reszta} ${odmiana(reszta, ["inny", "inne", "innych"])}` : ""}.`;
};

/**
 * WYSZUKIWARKA TYPÓW — szuka WYŁĄCZNIE w tym, co przysłał serwer (lista jest już zawężona do wniosków, które
 * pracownikowi przysługują), po nazwie, skrócie, opisie i nazwie kategorii. Zwraca pary { typ, kategoria }.
 */
export const szukajTypowWnioskow = (kategorie, fraza) => {
    const q = tekst(fraza).toLocaleLowerCase("pl");
    if (!q) return [];
    const wynik = [];
    (Array.isArray(kategorie) ? kategorie : []).forEach((kategoria) => {
        (kategoria?.types || []).forEach((typ) => {
            const siano = [typ?.label, typ?.shortLabel, typ?.description, kategoria?.label]
                .map((v) => tekst(v).toLocaleLowerCase("pl"))
                .join(" ");
            if (siano.includes(q)) wynik.push({ typ, kategoria });
        });
    });
    return wynik;
};

const DATA_ISO = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/;
const dataPl = (v) => {
    const m = DATA_ISO.exec(String(v || ""));
    if (!m) return String(v);
    return m[4] ? `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]}` : `${m[3]}.${m[2]}.${m[1]}`;
};
const etykietaOpcji = (pole, wartosc) => {
    const opcja = (pole?.options || []).find((o) => (typeof o === "string" ? o : o?.value) === wartosc);
    if (!opcja) return String(wartosc);
    return typeof opcja === "string" ? opcja : (opcja.label ?? opcja.value);
};

/**
 * WARTOŚĆ POLA W PODSUMOWANIU — tak, jak człowiek ją wpisał/wybrał (etykieta opcji, nazwa osoby, nazwa pliku,
 * data po polsku). Pusta wartość = pusty napis (podsumowanie takiego wiersza nie pokazuje).
 */
export const wartoscPolaDoPodsumowania = (pole, wartosc, formData = {}) => {
    if (wartosc === undefined || wartosc === null || wartosc === "") return "";
    // [polecenie właściciela 2026-09-30, A] Deklaracja „Bez osoby zastępującej” zostaje na podsumowaniu jako wiersz.
    if (czyBrakOsoby(wartosc)) return TEKST_BRAKU_OSOBY;
    switch (pole?.type) {
        case FIELD_TYPE.CHECKBOX:
            return wartosc === true ? "Tak" : "Nie";
        case FIELD_TYPE.FILE:
            return wartosc?.name || String(wartosc);
        case FIELD_TYPE.REPEATER: {
            const n = Array.isArray(wartosc) ? wartosc.length : 0;
            return n === 0 ? "" : `${n} ${odmiana(n, ["pozycja", "pozycje", "pozycji"])}`;
        }
        case FIELD_TYPE.SELECT:
            return etykietaOpcji(pole, wartosc);
        case FIELD_TYPE.MULTISELECT:
            return (Array.isArray(wartosc) ? wartosc : [wartosc]).map((w) => etykietaOpcji(pole, w)).join(", ");
        case FIELD_TYPE.DATE:
        case FIELD_TYPE.DATETIME:
            return dataPl(wartosc);
        case FIELD_TYPE.MONEY: {
            const waluta = tekst(formData?.[`${pole.name}Waluta`]) || "PLN";
            return `${wartosc} ${waluta}`;
        }
        default:
            break;
    }
    if (typeof wartosc === "object") {
        return tekst(wartosc.name) || tekst(wartosc.displayName) || tekst(wartosc.fullName) || tekst(wartosc.label) || "";
    }
    return String(wartosc);
};
